import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { useNotification } from '@app/AppLayout/NotificationContext';
import { AddressAsReceived } from '@app/components/AddressAsReceived';
import { DetailPageHeader } from '@app/components/DetailPageHeader';
import { FormAddress } from '@app/components/FormAddress';
import InlineEdit from '@app/components/InLineEdit';
import type { CourseVersion } from '@app/model/CourseVersion';
import { CLIENT_DELIVERY, HARDWARE_LEVEL, KLASS_DECISION, KLASS_TYPE } from '@app/model/Enums';
import { getFacilityCapabilities } from '@app/model/Facility';
import { Instructor } from '@app/model/Instructor';
import { AdHocKlass, Klass, PrivateKlass, expectedInstructorCount, hasMissingFinalInstructors, isKlassEditable } from '@app/model/Klass';
import { WORKING_WEEK_FRIDAY_SATURDAY_DEFAULT, expectedDeliveryDays, hasRecordedDeliveryPattern, parseDeliveredDays, parseWorkingWeek, requiresDeliveredDays, spanWorkingDays } from '@app/model/DeliveryPattern';
import { DeliveryDaysField } from '@app/components/DeliveryDaysField';
import { CLASS_EDIT_PERIOD_WEEKS } from '@app/utils/config';
import { currencyName, ddMMyyyyFormat, ddMMyyyyHHmmFormat, ddMMyyyyHHmmTzFormat, displayAddress, formatMoney, numberOfDays } from '@app/utils/functions';
import { ActionList, ActionListItem, Alert, AlertVariant, Button, Checkbox, DescriptionList, DescriptionListDescription, DescriptionListGroup, DescriptionListTerm, Divider, FlexItem, Icon, Label, Modal, ModalBody, ModalFooter, ModalHeader, ModalVariant, PageSection, Tab, TabTitleText, Tabs, Title, Tooltip } from '@patternfly/react-core';
import { ExclamationCircleIcon, ExclamationTriangleIcon, LockIcon, OutlinedQuestionCircleIcon } from '@patternfly/react-icons';
import { ClassSurveyOverview } from '@app/Classes/ClassSurveyOverview';
import { EntityChangeLog } from '@app/components/EntityChangeLog';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import * as React from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { KlassApi } from '@app/api';
import { KlassAutomationMessages, useKlassAutomationMessages } from '@app/components/KlassAutomationMessages';
import { KlassFlagSummary } from '@app/components/KlassFlagSummary';
import { useInstructorOptions } from '@app/components/InstructorName';
import { ClassDecisionLabel, ClassModalityLabel, ClassRegionLabel, ClassStatusLabel, ClassTypeLabel, InstructorNames, PaymentModeLabel, RequestStatusLabel, formatStatusText } from '@app/components/KlassLabels';
import { useEntity } from '@app/hooks/useEntity';
import { Address } from '@app/model/Address';
import { loadKlassByOfferingId } from '@app/store/entityLookups';
import { useClientExecutiveStore, useCourseStore, useInstructorStore, useKlassStore } from '@app/store/entityStore';
import { PARAMETER, useParameterStringList } from '@app/store/parameterStore';
import { NotFoundState } from '@app/components/NotFoundState';
import { isNotFound } from '@app/utils/ApiError';
import { hasRealmRole } from '@app/utils/keycloak';
import { ROLE } from '@app/utils/roles';

/**
 * The tabs, by the eventKey each has always had. Numbers rather than names because they are what
 * the tabs below were written with, and renumbering them would silently change which tab a reader
 * with the page open lands on after a deploy.
 *
 * Deliberately not contiguous: 2 and 4 belong to the two type-specific tabs, which are rendered
 * only for a class of that type.
 */
const TAB = { OVERVIEW: 0, DETAILS: 1, AD_HOC: 2, SURVEY: 3, PRIVATE: 4 } as const;

interface PrivateClassContentProps {
  pklass: PrivateKlass;
  updateKlass: <T extends Klass>(klass: T) => void;
  /** Drops the one editable field on this tab, the delivery address. */
  isReadOnly?: boolean;
}

const PrivateClassContent: React.FC<PrivateClassContentProps> = ({ pklass, updateKlass, isReadOnly = false }) => (
  <PageSection hasBodyWrapper isWidthLimited>
    <DescriptionList columnModifier={{ default: '3Col' }}>
      <DescriptionListGroup>
        <DescriptionListTerm>Private SKU</DescriptionListTerm>
        <DescriptionListDescription>{pklass.privateCourse?.sku}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>SKU sold days</DescriptionListTerm>
        <DescriptionListDescription>{pklass.daysSold}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Delivery Address</DescriptionListTerm>
        <DescriptionListDescription>
          <FormAddress value={pklass.deliveryAddress} id="deliveryAddress" name="deliveryAddress" isDisabled={isReadOnly} onChange={(_e, v) => updateKlass({ ...pklass, deliveryAddress: v })} />
          {/* Under the structured address, and collapsed: the evidence trail rather than a field.
              It carries no warning of its own - it is present on every imported class now, whether
              the address could be read or not, and the class that could not be read is flagged in
              the Location column and reported in the automation messages panel. */}
          <AddressAsReceived value={pklass.deliveryAddressText} />
        </DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Request ID</DescriptionListTerm>
        <DescriptionListDescription>{pklass.requestId}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Is english an option?</DescriptionListTerm>
        <DescriptionListDescription>{pklass.englishOption ? 'Yes' : 'No'}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Scoping Call ID</DescriptionListTerm>
        <DescriptionListDescription>{pklass.scopeCallRequestId}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Scoping Folder</DescriptionListTerm>
        <DescriptionListDescription>
          {/* Normally a link to where the paperwork is kept, but the source system is free to put
              anything in there, so whatever is not a link is shown as it came. */}
          {pklass.scopingFolder?.startsWith('http')
            ? <Button variant='link' isInline component='a' href={pklass.scopingFolder} target='_blank'>Open folder</Button>
            : pklass.scopingFolder}
        </DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>RHCS ID</DescriptionListTerm>
        <DescriptionListDescription>{pklass.rhscId}</DescriptionListDescription>
      </DescriptionListGroup>
      {/* Currency and revenue are on the Details tab: every class carries them now, not only a
          private one. */}
      <DescriptionListGroup>
        <DescriptionListTerm>Ticket open date</DescriptionListTerm>
        <DescriptionListDescription>{ddMMyyyyFormat(pklass.ticketOpenDate)}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Ticket Close date</DescriptionListTerm>
        <DescriptionListDescription>{ddMMyyyyFormat(pklass.ticketCloseDate)}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup className='multi-line-desc-group'>
        <DescriptionListTerm>Fees</DescriptionListTerm>
        <DescriptionListDescription className='multi-line-desc-list'>
          <DescriptionList isHorizontal isFluid isCompact>
            <DescriptionListGroup>
              <DescriptionListTerm>Rush</DescriptionListTerm>
              <DescriptionListDescription>{pklass.rushFee ? 'Yes' : 'No'}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Facility</DescriptionListTerm>
              <DescriptionListDescription>{pklass.facilityFee ? 'Yes' : 'No'}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Cancelation</DescriptionListTerm>
              <DescriptionListDescription>{pklass.cancellationFee ? 'Yes' : 'No'}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Preparation</DescriptionListTerm>
              <DescriptionListDescription>{pklass.preparationFee ? 'Yes' : 'No'}</DescriptionListDescription>
            </DescriptionListGroup>
          </DescriptionList>
        </DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup className='multi-line-desc-group'>
        <DescriptionListGroup>
          <DescriptionListTerm>Customer Name</DescriptionListTerm>
          <DescriptionListDescription>{pklass.customerName ? pklass.customerName : 'N/A'}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>SPOC</DescriptionListTerm>
          <DescriptionListDescription className='multi-line-desc-list'>
            <DescriptionList isHorizontal isFluid isCompact>
              <DescriptionListGroup>
                <DescriptionListTerm>Name</DescriptionListTerm>
                <DescriptionListDescription>{pklass.spocName}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Email</DescriptionListTerm>
                <DescriptionListDescription>{pklass.spocEmail}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Phone</DescriptionListTerm>
                <DescriptionListDescription>{pklass.spocPhone}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Contacted?</DescriptionListTerm>
                <DescriptionListDescription>{pklass.spocConnected ? 'Yes' : 'No'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Response</DescriptionListTerm>
                <DescriptionListDescription>{pklass.spocResponse}</DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Participants (RHN ID)</DescriptionListTerm>
        <DescriptionListDescription>{pklass.participantsRHNID?.length ? pklass.participantsRHNID.join(', ') : 'N/A'}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Payment Mode</DescriptionListTerm>
        <DescriptionListDescription><PaymentModeLabel paymentMode={pklass.paymentMode} /></DescriptionListDescription>
      </DescriptionListGroup>
    </DescriptionList>
  </PageSection>
);

/**
 * The request half of an ad hoc class: where it came from, who asked, who approved, and what was
 * asked for. None of it duplicates the class fields above - see the AdHocKlass model for which
 * near-misses are deliberate, in particular that `delegatesInterested` is a claim on the request
 * while the delegate counts on the Details tab are what the class actually sold.
 *
 * Read only, like the Private tab: every one of these fields is written by the ad hoc import, and
 * the class fields somebody works on - the instructors, the decision, the release - are on the page
 * above and editable there.
 */
const AdHocClassContent: React.FC<{ aklass: AdHocKlass }> = ({ aklass }) => (
  <PageSection hasBodyWrapper isWidthLimited>
    <DescriptionList columnModifier={{ default: '3Col' }}>
      <DescriptionListGroup>
        <DescriptionListTerm>Request ID</DescriptionListTerm>
        <DescriptionListDescription>{aklass.requestId || 'N/A'}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Request type</DescriptionListTerm>
        <DescriptionListDescription>{aklass.requestType || 'N/A'}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Requested by</DescriptionListTerm>
        <DescriptionListDescription>
          {aklass.requester ? formatStatusText(aklass.requester) : 'N/A'}
        </DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Ticket open date</DescriptionListTerm>
        <DescriptionListDescription>
          {aklass.ticketOpenDate ? ddMMyyyyHHmmTzFormat(aklass.ticketOpenDate) : 'N/A'}
        </DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Request status</DescriptionListTerm>
        <DescriptionListDescription><RequestStatusLabel status={aklass.requestStatus} /></DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>TSS approval</DescriptionListTerm>
        <DescriptionListDescription><RequestStatusLabel status={aklass.tssApproval} /></DescriptionListDescription>
      </DescriptionListGroup>
      {/* Why the request was turned down, which is a different thing from why a class that already
          existed was cancelled - that one is on the Details tab. */}
      {aklass.declinedReason && (
        <DescriptionListGroup>
          <DescriptionListTerm>Reason declined</DescriptionListTerm>
          <DescriptionListDescription>{aklass.declinedReason}</DescriptionListDescription>
        </DescriptionListGroup>
      )}
      <DescriptionListGroup>
        <DescriptionListTerm>Customer Name</DescriptionListTerm>
        <DescriptionListDescription>{aklass.customerName || 'N/A'}</DescriptionListDescription>
      </DescriptionListGroup>
      {/* Delivery responsibility is on the Details tab: every class carries it now, not only an
          ad hoc one. */}
      <DescriptionListGroup>
        <DescriptionListTerm>
          <span>Delegates interested</span>{' '}
          <Tooltip content="The headcount the request asked for. The delegate counts on the Details tab are what the class has actually sold.">
            <Icon status="info"><OutlinedQuestionCircleIcon /></Icon>
          </Tooltip>
        </DescriptionListTerm>
        <DescriptionListDescription>{aklass.delegatesInterested ?? 'N/A'}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Enrolments processed</DescriptionListTerm>
        <DescriptionListDescription>{aklass.enrolmentsProcessed ?? 'N/A'}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Payment order available?</DescriptionListTerm>
        <DescriptionListDescription>{aklass.paymentOrderAvailable ? 'Yes' : 'No'}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Open allocation?</DescriptionListTerm>
        <DescriptionListDescription>{aklass.openAllocation ? 'Yes' : 'No'}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Resource booking date</DescriptionListTerm>
        <DescriptionListDescription>{ddMMyyyyFormat(aklass.resourceBookingDate) || 'N/A'}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>NIIT case ID</DescriptionListTerm>
        <DescriptionListDescription>{aklass.niitCaseId || 'N/A'}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Secondary notification email</DescriptionListTerm>
        <DescriptionListDescription>{aklass.secondaryNotificationEmail || 'N/A'}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Justification</DescriptionListTerm>
        <DescriptionListDescription>{aklass.justification || 'N/A'}</DescriptionListDescription>
      </DescriptionListGroup>
      <DescriptionListGroup>
        <DescriptionListTerm>Special requests</DescriptionListTerm>
        <DescriptionListDescription>{aklass.specialRequests || 'N/A'}</DescriptionListDescription>
      </DescriptionListGroup>
    </DescriptionList>
  </PageSection>
);


interface ClassDetailProps {
  /**
   * Rendered under Classes History, where a class is a record of what happened rather than
   * something being worked on. Set by the route, so the component does not have to read the
   * URL to know which of the two it is.
   */
  isHistory?: boolean;
}

const ClassDetail: React.FunctionComponent<ClassDetailProps> = ({ isHistory = false }) => {
  type Action = { type: 'load' | 'update', payload: Klass };
  const { oid } = useParams();
  const { addNotification } = useNotification();
  const navigate = useNavigate();

  const { items: instructors } = useEntity(useInstructorStore);
  const { items: clientExecutives } = useEntity(useClientExecutiveStore);
  const { items: courses } = useEntity(useCourseStore);

  const saveKlass = useKlassStore(state => state.save);

  /**
   * Undoing a release is an administrator's correction, not part of the normal flow, so the
   * shortcut is only offered to one. The service checks the role itself - this only decides
   * what the page puts on screen.
   */
  const isAdmin = hasRealmRole(ROLE.ADMIN);

  /**
   * The class's survey page - every answer by question and by respondent.
   *
   * Built from `oid` rather than from the current address so the history variant of this page
   * keeps its own trail: a reader who arrived from the History search goes to
   * /classes/history/4711/survey and comes back to the search, not to the editable class.
   *
   * **Admin only, and the route enforces it** - see routes.tsx. The links to it are offered on the
   * same condition so that nobody is handed one that answers with the forbidden page.
   */
  const surveyPath = `/classes/${isHistory ? 'history/' : ''}${oid}/survey`;

  const [activeTabKey, setActiveTabKey] = React.useState<string | number>(TAB.OVERVIEW);
  const [isBox] = React.useState<boolean>(false);
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  const [releaseConfirmOpen, setReleaseConfirmOpen] = React.useState<boolean>(false);
  const [undoReleaseConfirmOpen, setUndoReleaseConfirmOpen] = React.useState<boolean>(false);
  const [validationErrors, setValidationErrors] = React.useState({ decision: false, finalInstructor: false, deliveredDays: false });
  /** The Details tab shows the delegate breakdown on a tooltip until it is clicked open, then inline. */
  const [delegatesExpanded, setDelegatesExpanded] = React.useState<boolean>(false);
  const [changeLogOpen, setChangeLogOpen] = React.useState<boolean>(false);
  /**
   * The `Irregular delivery pattern` box while the user is holding it open over an empty pair of
   * fields - the one state the class itself cannot express, there being no such column. Null means
   * "whatever the class says", which is what every fresh load goes back to.
   */
  const [irregularOverride, setIrregularOverride] = React.useState<boolean | null>(null);

  const klassUpdater = (state: Klass, action: Action) => {
    switch (action.type) {
      case 'load': return action.payload;
      case 'update': 
        setIsDirty(true);
        return action.payload;
      default: return state;
    }
  };
  
  const [klass, setKlass] = React.useReducer(klassUpdater, {} as Klass);

  /** No class carries this offering id, so the page says so in place of the record. */
  const [notFound, setNotFound] = React.useState<boolean>(false);

  const updateKlass = React.useCallback(<T extends Klass>(updatedKlass: T) => {
    setKlass({ type: 'update', payload: updatedKlass });
  }, []);

  /** A class arriving from the service, replacing whatever was on the page. */
  const loadKlass = React.useCallback((payload: Klass) => {
    setKlass({ type: 'load', payload });
    // The box goes back to reading the class. Left set, a cancelled edit would reopen the fields
    // on a class that no longer has anything in them.
    setIrregularOverride(null);
  }, []);

  /** `bypassCache` is what Refresh passes: read the class from the service whatever is held. */
  const fetchClassData = async (bypassCache = false) => {
    if (!oid) return;
    setNotFound(false);
    try {
      // Uses the repo, which instantly returns from memory if available!
      const data = await loadKlassByOfferingId(oid, bypassCache);
      loadKlass(data);
      setIsDirty(false);
    } catch (error: any) {
      // A mistyped or stale offering id is not a failure to report, it is the answer:
      // the page below renders it, so there is nothing for a toast to add.
      if (isNotFound(error)) {
        setNotFound(true);
        return;
      }
      console.error("Error fetching class data:", error);
      addNotification(error.message, AlertVariant.danger, `Error fetching class data`);
    }
  };

  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = ''; // Required by modern browsers to show the native warning prompt
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname
  );
    
  React.useEffect(() => {
    fetchClassData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oid]);

  const facilityCapabilities = React.useMemo(() => {
    return getFacilityCapabilities(klass.facility?.rooms);
  }, [klass.facility?.rooms]);

  // What the imports could not make sense of about this class, shown in the title bar. Read here
  // rather than by the component that shows it, which lives inside the portalled header and is
  // mounted twice for it - see useKlassAutomationMessages.
  const automationMessages = useKlassAutomationMessages(klass);

  // --- Instructor pickers -----------------------------------------------------------
  // Both instructor fields are lists, edited through the same option set. The selects hand
  // back ids, so they are mapped to the store's instructors on the way in and out.
  // Built per course, and carrying each instructor's certification for it - see
  // useInstructorOptions. Both pickers pass what they already hold, so an instructor who has
  // since gone inactive is not dropped from a class they are already on.
  const instructorOptionsFor = useInstructorOptions(instructors);

  const toInstructors = React.useCallback(
    (ids: any): Instructor[] =>
      ((ids ?? []) as (string | number)[])
        .map(id => instructors.find(i => i.id === id))
        .filter(Boolean) as Instructor[],
    [instructors]
  );

  const allocatedInstructorIds = React.useMemo(
    () => (klass.allocatedInstructors ?? []).map(i => i.id),
    [klass.allocatedInstructors]
  );

  const finalInstructorIds = React.useMemo(
    () => (klass.finalInstructors ?? []).map(i => i.id),
    [klass.finalInstructors]
  );

  // --- Course picker ----------------------------------------------------------------
  // Searchable by either half of what names a course: the typeahead filters on the option
  // text, so 'RH124' and 'System Administration' both find it.
  const courseOptions = React.useMemo(
    () => courses.map(c => ({ value: c.id, content: `${c.sku} - ${c.name}` })),
    [courses]
  );

  /**
   * Points the class at another course, and at that course's own version with it.
   *
   * The version is what the rest of the Course block reads - version number, extended SKU, hours
   * per day, number of days, hardware level - so carrying the old one over would leave the page
   * describing two different courses, and the class stored that way. The new course's latest
   * version is what it takes, the same one an imported class arrives with; a course with no
   * version to give leaves the field empty rather than keeping another course's. Either way the
   * version picker below is then offering the new course's versions, so the pair cannot drift.
   */
  const onCourseChange = React.useCallback((courseId: string | number) => {
    const course = courses.find(c => c.id === courseId);
    if (!course) return;
    const version = course.latestVersion ?? course.versions?.[0];
    updateKlass({ ...klass, course, version: version as CourseVersion });
  }, [courses, klass, updateKlass]);

  /**
   * The versions on offer: the ones belonging to the course the class is on, newest first.
   *
   * Read from the store's copy of that course rather than from the class's own, which carries
   * only the version the class is actually on - the class page has no other way to know what
   * else the course was published as. The version the class already holds is kept on the list
   * whatever the catalogue now says, so opening the field on a class sitting on a withdrawn
   * version does not quietly leave it with nothing to go back to.
   */
  const versionsOffered = React.useMemo(() => {
    const course = courses.find(c => c.id === klass.course?.id) ?? klass.course;
    const offered = [...(course?.versions ?? [])];
    if (klass.version?.id && !offered.some(v => v.id === klass.version.id)) offered.push(klass.version);

    // Newest first, by the date the version was released. One without a date sorts last: it is
    // either unreleased or unrecorded, and neither belongs above the versions being taught.
    const released = (v: CourseVersion) => (v.releaseDate ? new Date(v.releaseDate).getTime() : -Infinity);
    return offered.sort((a, b) => released(b) - released(a));
  }, [courses, klass.course, klass.version]);

  // Named as the Course Versions table names them - the number, then what the catalogue says of
  // it. The status is what tells a retired version from the one being taught.
  const versionOptions = React.useMemo(
    () => versionsOffered.map(v => ({
      value: v.id,
      content: v.status ? `${v.version} - ${formatStatusText(v.status)}` : `${v.version}`
    })),
    [versionsOffered]
  );

  const onVersionChange = React.useCallback((versionId: string | number) => {
    const version = versionsOffered.find(v => v.id === versionId);
    if (!version) return;
    updateKlass({ ...klass, version });
  }, [versionsOffered, klass, updateKlass]);

  // --- Delivery pattern -------------------------------------------------------------
  // Whether the class's dates can account for the days it was sold as. The same question the
  // service's `Non standard delivery pattern` flag asks - see model/DeliveryPattern, which is a
  // port of the rule it is evaluated with - so what this page insists on is exactly what the flag
  // is raised against, and filling the field clears both.
  const workingWeekEntries = useParameterStringList(
    PARAMETER.WORKING_WEEK_FRIDAY_SATURDAY, WORKING_WEEK_FRIDAY_SATURDAY_DEFAULT);

  const workingWeek = React.useMemo(() => parseWorkingWeek(workingWeekEntries), [workingWeekEntries]);

  // The catalogue's copy of the course rather than the class's own, for the same reason
  // versionsOffered reads it there: a bundle's expected days come from the halves hanging off it,
  // which the class carries no deeper than it needs to.
  const catalogueCourse = React.useMemo(
    () => courses.find(c => c.id === klass.course?.id) ?? klass.course,
    [courses, klass.course]
  );

  /** Set while the class's dates do not describe its delivery and nobody has said what did. */
  const deliveredDaysRequired = React.useMemo(
    () => requiresDeliveredDays(klass, workingWeek, catalogueCourse),
    [klass, workingWeek, catalogueCourse]
  );

  /** What the span and the course say, for the message that asks for the correction. */
  const deliverySpan = React.useMemo(
    () => ({
      span: spanWorkingDays(klass, workingWeek),
      expected: expectedDeliveryDays(klass, catalogueCourse)
    }),
    [klass, workingWeek, catalogueCourse]
  );

  /**
   * Whether the delivery fields are open: what the class already records, unless the user has taken
   * hold of the box - which is the only way to open an empty pair of them.
   */
  const irregularDelivery = irregularOverride ?? hasRecordedDeliveryPattern(klass);

  /**
   * Clearing the box throws both fields away, rather than hiding what they hold.
   *
   * The alternative is worse than it looks: a class saved with the box clear but a count still on it
   * would go on being counted from that figure by utilization, with nothing on the page left saying
   * so. What is hidden here is what is gone.
   */
  const onIrregularDeliveryChange = (checked: boolean) => {
    setIrregularOverride(checked);
    if (!checked && hasRecordedDeliveryPattern(klass)) {
      updateKlass({ ...klass, deliveredDays: null, deliveryDays: [] });
    }
    setValidationErrors(prev => ({ ...prev, deliveredDays: false }));
  };

  // A release that names fewer final instructors than the class was scheduled or allocated
  // for leaves someone off the delivery, so the confirmation spells that out first.
  const missingFinalInstructors = hasMissingFinalInstructors(klass);

  const handleTabClick = (_e: any, tabIndex: string | number) => setActiveTabKey(tabIndex);
  // Bypasses the cached copy so the page shows what the backend has now. Goes through the
  // same load as the first one, so a class deleted since the page opened is reported the
  // same way rather than rejecting into nothing.
  const onRefresh = () => fetchClassData(true);

  const onCancelEdit = () => {
    if (isDirty) fetchClassData();
  };

  /**
   * @returns whether the class was written. False covers both a refused save and a rejected one,
   *     which is what the release path needs: it saves any pending edit first and must not go on to
   *     release a class whose edit never landed.
   */
  const onSave = async (): Promise<boolean> => {
    // The dates do not describe the delivery and nobody has said what did. Refused here rather
    // than left to the service, which accepts it and raises a flag instead - the person who has
    // the class open is the one who knows what happened to it.
    if (deliveredDaysRequired) {
      setValidationErrors(prev => ({ ...prev, deliveredDays: true }));
      addNotification(
        `This class spans ${deliverySpan.span} working ${deliverySpan.span === 1 ? 'day' : 'days'} but was sold as ${deliverySpan.expected}. Set the delivered days to say how many days it really took.`,
        AlertVariant.danger,
        "Delivered days is required"
      );
      return false;
    }

    beginLoading();
    try {
      // 1. The store handles the API PUT, data ingestion, AND global cache update
      const updatedKlass = await saveKlass(klass, false);
      
      // 2. Update local reducer state
      loadKlass(updatedKlass);
      setIsDirty(false);

      addNotification("Class saved with success!", AlertVariant.success);
      return true;
    } catch (error: any) {
      addNotification(error.message, AlertVariant.danger, "Error saving class");
      console.error(error);
      return false;
    } finally {
      endLoading();
    }
  };

  const handleInitiateRelease = () => {
    const isDecisionMissing = !klass.decision;
    const isFinalInstructorMissing = !klass.finalInstructors?.length;

    if (isDecisionMissing || isFinalInstructorMissing) {
      setValidationErrors(prev => ({ ...prev, decision: isDecisionMissing, finalInstructor: isFinalInstructorMissing }));
      addNotification(
        "Missing Required Fields",
        AlertVariant.danger,
        "You must set both the Decision and at least one Final Instructor before releasing this class."
      );
      return;
    }

    // Clear any previous errors and open the modal
    setValidationErrors({ decision: false, finalInstructor: false, deliveredDays: false });
    setReleaseConfirmOpen(true);
  };

const handleReleaseConfirm = async () => {
    if (!oid) return; 

    beginLoading();
    try {
      // 1. Save any pending edits first (this now uses our clean onSave above!). A save that was
      //    refused or rejected stops the release: releasing a class whose edit never landed would
      //    send the instructors a line-up nobody agreed to. The finally below still closes the
      //    modal and ends the loading state.
      if (isDirty && !(await onSave())) return;
      
      // 2. Trigger custom backend business logic
      await KlassApi.release(klass.id);

      // 3. Fetch fresh state bypassing cache
      const freshKlass = await loadKlassByOfferingId(oid, true);
      
      // 4. Update local state
      loadKlass(freshKlass);
      
      // 5. Safely update the cache using the built-in upsertItem
      useKlassStore.getState().upsertItem(freshKlass);

      addNotification('Class released successfully', AlertVariant.success);
    } catch (error: any) {
      addNotification(error.message, AlertVariant.danger, "Error releasing class");
    } finally {
      setReleaseConfirmOpen(false);
      endLoading();
    }
  };

  /**
   * Takes the release back: flags the class as not released and writes it straight away.
   *
   * Only the flag is undone. Whatever the release set off downstream - the notification to the
   * instructors above all - has already left, which is what the confirmation warns about; that
   * side of it is somebody's phone call, not this button's. Any edit left pending on the page
   * goes out with the write, the same as releasing does.
   */
  const handleUndoReleaseConfirm = async () => {
    beginLoading();
    try {
      const updatedKlass = await saveKlass({ ...klass, released: false }, false);
      loadKlass(updatedKlass);
      setIsDirty(false);

      addNotification('Class release undone', AlertVariant.success);
    } catch (error: any) {
      addNotification(error.message, AlertVariant.danger, "Error undoing the class release");
      console.error(error);
    } finally {
      setUndoReleaseConfirmOpen(false);
      endLoading();
    }
  };

  const tooltip = <Tooltip content="Aria-disabled tabs are like disabled tabs, but focusable. Allows for tooltip support." />;

  /**
   * The address's city and country, falling back to the class's own pair when the address has none.
   *
   * The fallback is what a class carrying no facility and no delivery address has to show: those
   * arrive on the class itself, so an ad hoc class whose room is not booked yet still says where it
   * is meant to be delivered rather than reading 'N/A'.
   */
  const renderCityCountry = (address : Address | undefined) => {
    if (address?.city && address.country) {
      return address.city + "/" + address.country.name;
    }
    const own = [klass.city, klass.country?.name].filter(Boolean).join('/');
    return own || 'N/A';
  }

  // Nothing answers to that offering id. Said here, in the middle of the page, with the way
  // back to the list the user came from - the history list when that is where they started.
  if (notFound) {
    return (
      <NotFoundState
        entityLabel="class"
        identifierLabel="offering ID"
        identifier={oid}
        backTo={isHistory
          ? { path: '/classes/history', label: 'Back to class history' }
          : { path: '/classes', label: 'Back to classes' }}
      />
    );
  }

  if (!klass.id && !klass.offeringId) return null;

  // --- Read only mode ---------------------------------------------------------------
  // Two ways in. Opened from Classes History the page is always a record: read only
  // whatever the class's date. Opened at /classes/:oid it is the working page, unless the
  // class has aged out of the edit period - which a saved link can still reach even though
  // the Classes list no longer lists it.
  const isEditable = isKlassEditable(klass);
  const isReadOnly = isHistory || !isEditable;

  // A class still inside the period, being read from History: offer the working page
  // rather than making the user find it again. Unreachable at /classes/:oid, where
  // read only means the class is past the period and there is nowhere to forward to.
  const canForwardToEdit = isReadOnly && isEditable;

  const readOnlyReason = isEditable
    ? 'Opened from the class history. Use Edit to open this class for changes.'
    : `This class started more than ${CLASS_EDIT_PERIOD_WEEKS} weeks ago and can no longer be changed.`;

  // Theme-aware highlight for fields blocking a release. The tint is derived from the
  // danger token so it follows light/dark instead of being a fixed pink.
  const validationErrorStyle: React.CSSProperties = {
    border: '2px dashed var(--pf-t--global--border--color--status--danger--default)',
    padding: '8px',
    borderRadius: '4px',
    backgroundColor: 'color-mix(in srgb, var(--pf-t--global--color--status--danger--default) 12%, transparent)'
  };

  const validationErrorTextStyle: React.CSSProperties = {
    color: 'var(--pf-t--global--text--color--status--danger--default)',
    fontSize: '0.875rem',
    marginTop: '8px'
  };

  // The delegate breakdown on the Details tab reads the same whether it is hovered on the
  // tooltip or clicked open below the total, so both spellings render the one block. Laid out
  // in two columns: eleven lines stacked is taller than either place has room for.
  const delegatesBreakdown = (
    <div style={{ textAlign: 'left', lineHeight: '1.5', display: 'grid', gridTemplateColumns: 'auto auto', columnGap: '24px' }}>
      <div>Paying: {klass.payingDelegates || 0}</div>
      <div>RHLS Premium: {klass.rhlsPremiumDelegates || 0}</div>
      <div>RHLS Course: {klass.rhlsCourseDelegates || 0}</div>
      <div>Training Units: {klass.trainingUnitsDelegates || 0}</div>
      <div>Free: {klass.freeDelegates || 0}</div>
      <div>Internal: {klass.internalDelegates || 0}</div>
      <div>Bundled: {klass.bundledDelegates || 0}</div>
      <Divider style={{ margin: '8px 0', gridColumn: '1 / -1' }} />
      <div><strong>Booked: {klass.delegatesBooked || 0}</strong></div>
      <div><strong>Total: {klass.totalEnrolled || 0}</strong></div>
      <div><strong>Max: {klass.maxDelegates || 0}</strong></div>
    </div>
  );

  const toggleDelegates = () => setDelegatesExpanded((expanded) => !expanded);

  const delegatesSummary = (
    <span
      className="expandable-text"
      role="button"
      tabIndex={0}
      aria-expanded={delegatesExpanded}
      onClick={toggleDelegates}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleDelegates();
        }
      }}
    >
      {klass.totalEnrolled || 0} / {klass.maxDelegates || 0}
    </span>
  );

  const changeLogTitle = `${klass.offeringId} - ${klass.course?.sku || ""}`;

  return (
    <EntityChangeLog
      entity="class"
      id={klass.id}
      entityLabel="this class"
      title={changeLogTitle}
      isOpen={changeLogOpen}
      onClose={() => setChangeLogOpen(false)}
    >
      <DetailPageHeader
        title={changeLogTitle}
        actions={
          <ActionList>
            {canForwardToEdit && (
              <ActionListItem>
                {/* The working copy of the same class, where everything below is editable.
                    backPath keeps the way out pointing at the history search the user came
                    from: without it the layout's Back would send them to the Classes list,
                    which is not where they were and does not hold this class. */}
                <Button variant="primary" id="edit-button"
                  onClick={() => navigate('/classes/' + oid, { state: { backPath: '/classes/history' } })}>
                  Edit
                </Button>
              </ActionListItem>
            )}
            {!isReadOnly && (
              <React.Fragment>
                <ActionListItem>
                  <Button variant="primary" id="save-button" isDisabled={!isDirty} onClick={onSave}>Save</Button>
                </ActionListItem>
                <ActionListItem>
                  <Button variant="link" id="cancel-button" isDisabled={!isDirty} onClick={onCancelEdit}>Cancel</Button>
                </ActionListItem>
              </React.Fragment>
            )}
            <ActionListItem>
              <PageActionsMenu
                onRefresh={onRefresh}
                items={[
                  {
                    key: 'change-log',
                    label: 'Change log',
                    onClick: () => setChangeLogOpen(true)
                  },
                  // Reachable from the Survey tab as well. Here too because the tab has to be
                  // opened and read before its own link is visible, and somebody who already knows
                  // they want the submissions should not have to go through it.
                  ...(isAdmin
                    ? [{
                        key: 'survey-submissions',
                        label: 'Survey submissions',
                        onClick: () => navigate(surveyPath)
                      }]
                    : [])
                ]}
              />
            </ActionListItem>
          </ActionList>
        }
      >
        <FlexItem>
          <ClassModalityLabel modality={klass.modality} />
        </FlexItem>
        <FlexItem>
          <ClassTypeLabel type={klass.type} />
        </FlexItem>
        {/* Renders nothing on a class the report named no operating unit for, which leaves the
            title reading as it did before this was added. */}
        <FlexItem>
          <ClassRegionLabel region={klass.region} />
        </FlexItem>
        {!isReadOnly && (
          <FlexItem>
            <KlassFlagSummary flags={klass.activeFlags} />
          </FlexItem>
        )}
        {isReadOnly && (
          <FlexItem>
            <Tooltip content={readOnlyReason}>
              <Label color="grey" variant="outline" icon={<LockIcon />}>Read only</Label>
            </Tooltip>
          </FlexItem>
        )}
        {/* Renders nothing unless the imports had something to say about this class, which is the
            normal case - and is why it sits at the end of the row rather than holding a place in
            it. Shown on a read only class as well: what the automation could not make sense of is
            worth reading whether or not the class can still be changed. */}
        <FlexItem>
          <KlassAutomationMessages offeringId={klass.offeringId} messages={automationMessages} />
        </FlexItem>
      </DetailPageHeader>

      <PageSection hasBodyWrapper isWidthLimited>
        <DescriptionList columnModifier={{ default: '3Col' }}>
          <DescriptionListGroup>
            <DescriptionListTerm>Offering ID</DescriptionListTerm>
            <DescriptionListDescription>{klass.offeringId}</DescriptionListDescription> 
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Class type</DescriptionListTerm>
            <DescriptionListDescription><ClassTypeLabel type={klass.type} /></DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Instructor{klass.instructors?.length > 1 ? 's' : ''}</DescriptionListTerm>
            <DescriptionListDescription><InstructorNames instructors={klass.instructors} course={klass.course} /></DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Start Date/Time</DescriptionListTerm>
            <DescriptionListDescription>{klass.startDateTime ? ddMMyyyyHHmmTzFormat(klass.startDateTime) : 'Not set'}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>End Date/Time</DescriptionListTerm>
            <DescriptionListDescription>{klass.endDateTime ? ddMMyyyyHHmmTzFormat(klass.endDateTime) : 'Not set'}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Allocated Instructors' type='MultiFilterableSelect'
              isReadOnly={isReadOnly}
              placeholder="Select one or more instructors"
              value={allocatedInstructorIds}
              decorator={() => <InstructorNames instructors={klass.allocatedInstructors} course={klass.course} />}
              onSave={(ids) => updateKlass({ ...klass, allocatedInstructors: toInstructors(ids) })}
              id="allocatedInstructors"
              options={instructorOptionsFor(klass.course, klass.allocatedInstructors)} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Language</DescriptionListTerm>
            <DescriptionListDescription>{klass.language?.englishName}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Modality</DescriptionListTerm>
            <DescriptionListDescription><ClassModalityLabel modality={klass.modality} /></DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Duration</DescriptionListTerm>
            <DescriptionListDescription>{numberOfDays(klass.startDateTime!, klass.endDateTime!)} days</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Guaranted to run?</DescriptionListTerm>
            <DescriptionListDescription>{klass.guarantedToRun ? 'Yes' : 'No'}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>City/Country</DescriptionListTerm>
            <DescriptionListDescription>
              {
                (klass.type === KLASS_TYPE.PRIVATE && (klass as PrivateKlass).customerLocation === true) ?
                  renderCityCountry((klass as PrivateKlass).deliveryAddress)
                :
                  renderCityCountry(klass.facility?.address)
              }
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Status</DescriptionListTerm>
            <DescriptionListDescription><ClassStatusLabel status={klass.status} /></DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <div style={validationErrors.decision ? validationErrorStyle : {}}>
              <InlineEdit label='Decision' type='FilterableSelect'
                isReadOnly={isReadOnly}
                value={klass.decision}
                decorator={(decision) => <ClassDecisionLabel decision={decision}/>}
                onSave={(decision) => {
                  // Confirming with no option picked clears the field. Send null rather
                  // than '' — the backend enum won't deserialize an empty string.
                  updateKlass({...klass, decision: (decision || null) as unknown as KLASS_DECISION});
                  setValidationErrors(prev => ({ ...prev, decision: false })); // <-- Clear error on change
                }}
                id="decision"
                options={Object.values(KLASS_DECISION).map(d => ({ value: d, content: d.replace(/_/g, ' ')}) )}/>
              
              {/* Error Helper Text */}
              {validationErrors.decision && (
                <div style={validationErrorTextStyle}>
                  <Icon status="danger"><ExclamationCircleIcon/></Icon> Decision is required for release
                </div>
              )}
            </div>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>
              Class released?
              {!isReadOnly && (
                <Button
                  variant="link"
                  isInline
                  style={{ marginLeft: '16px' }}
                  onClick={handleInitiateRelease}
                >
                  Release class
                </Button>
              )}
              {/* The way back out of a release, for an administrator only: there is nothing to
                  undo until the class has been released, and the correction is theirs to make. */}
              {!isReadOnly && klass.released && isAdmin && (
                <Button
                  variant="link"
                  isInline
                  id="undo-release-button"
                  style={{ marginLeft: '16px' }}
                  onClick={() => setUndoReleaseConfirmOpen(true)}
                >
                  Undo
                </Button>
              )}
            </DescriptionListTerm>
            <DescriptionListDescription>{klass.released ? 'Yes' : 'No'}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
             {/* Dynamic red wrapper for Final Instructor */}
            <div style={validationErrors.finalInstructor ? validationErrorStyle : {}}>
              <InlineEdit label='Final Instructors' type='MultiFilterableSelect'
                isReadOnly={isReadOnly}
                placeholder="Select one or more instructors"
                value={finalInstructorIds}
                decorator={() => <InstructorNames instructors={klass.finalInstructors} course={klass.course} />}
                onSave={(ids) => {
                  updateKlass({ ...klass, finalInstructors: toInstructors(ids) });
                  setValidationErrors(prev => ({ ...prev, finalInstructor: false })); // <-- Clear error on change
                }}
                id="finalInstructors" /* <-- Fixed ID clash */
                options={instructorOptionsFor(klass.course, klass.finalInstructors)} />

              {/* Error Helper Text */}
              {validationErrors.finalInstructor && (
                <div style={validationErrorTextStyle}>
                  <Icon status="danger"><ExclamationCircleIcon/></Icon> At least one Final Instructor is required for release
                </div>
              )}
            </div>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Client Executive' type='FilterableSelect'
              isReadOnly={isReadOnly}
              value={klass.clientExecutive?.id ?? ''}
              decorator={() => klass.clientExecutive?.name || 'N/A'}
              onSave={(id) => {
                klass.clientExecutive = clientExecutives.find(i => i.id === id)!;
                updateKlass({...klass});
              }}
              id="clientExecutive"
              options={clientExecutives.map(i => ({ value: i.id as number, content: i.name as string, selected: i.id === klass.clientExecutive?.id }))} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Released Confirmed?</DescriptionListTerm>
            <DescriptionListDescription>
              {klass.releaseConfirmed ? 'Yes' : 'No'}
              {/* Only a released class has anything to confirm, so the shortcut appears once the
                  release has gone through and disappears again as soon as it is confirmed. Like
                  every other field here it marks the page dirty; Save writes it. */}
              {!klass.releaseConfirmed && klass.released && !isReadOnly && (
                <React.Fragment>
                  {' - '}
                  <Button
                    variant="link"
                    isInline
                    id="confirm-release-button"
                    onClick={() => updateKlass({ ...klass, releaseConfirmed: true })}
                  >
                    Confirm it?
                  </Button>
                </React.Fragment>
              )}
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Client Delivery' type='FilterableSelect'
              isReadOnly={isReadOnly}
              value={klass.clientDelivery ?? ''}
              decorator={(clientDelivery) => clientDelivery ? formatStatusText(clientDelivery as string) : 'N/A'}
              onSave={(clientDelivery) => {
                // Same as Decision: an empty pick clears the field, and the backend enum
                // won't deserialize '' — send null.
                updateKlass({ ...klass, clientDelivery: (clientDelivery || null) as unknown as CLIENT_DELIVERY });
              }}
              id="clientDelivery"
              options={Object.values(CLIENT_DELIVERY).map(d => ({ value: d, content: formatStatusText(d) }))} />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit label='Comments' type='TextArea'
              isReadOnly={isReadOnly}
              value={klass.comments || ''}
              onSave={(comments) => updateKlass({...klass, comments: comments as string}) }
              id="comments"/>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Delivery Responsibility</DescriptionListTerm>
            <DescriptionListDescription>{klass.deliveryResponsibility}</DescriptionListDescription>
          </DescriptionListGroup>
          {/* Beside Duration, which is the span these correct. Folded away behind the checkbox
              because a standard delivery needs none of it, and most deliveries are standard - what
              singles out a class that does need it is the warning, which shows either way. */}
          {/* No multi-line-desc-group here, unlike the other blocks that hold a nested list: that
              class is `grid-row: 3 / 1`, which pins whatever carries it to the first row of the
              grid - so this one would jump to the top of the page however late it is written. */}
          <DescriptionListGroup>
            <div style={validationErrors.deliveredDays ? validationErrorStyle : {}}>
              <Checkbox
                id="irregularDelivery"
                label="Irregular delivery pattern"
                description="The class was not taught over the consecutive days its course is long."
                isChecked={irregularDelivery}
                isDisabled={isReadOnly}
                onChange={(_e, checked) => onIrregularDeliveryChange(checked)}
              />

              {/* The ask, and what prompted it. Shown as soon as the page opens rather than only
                  after a refused save: somebody who reads it first never meets the refusal. */}
              {deliveredDaysRequired && !isReadOnly && (
                <div style={validationErrors.deliveredDays ? validationErrorTextStyle : { fontSize: '0.875rem', marginTop: '8px' }}>
                  <Icon status={validationErrors.deliveredDays ? 'danger' : 'warning'}>
                    {validationErrors.deliveredDays ? <ExclamationCircleIcon /> : <ExclamationTriangleIcon />}
                  </Icon>{' '}
                  These dates span {deliverySpan.span} working {deliverySpan.span === 1 ? 'day' : 'days'} but the
                  class was sold as {deliverySpan.expected}
                  {deliverySpan.expected === 1 ? ' day' : ' days'}.{' '}
                  {irregularDelivery
                    ? 'Say how many days it really took before saving.'
                    : 'Tick the box above and say how many days it really took before saving.'}
                </div>
              )}

              {irregularDelivery && (
                <DescriptionListDescription className='multi-line-desc-list' style={{ marginTop: '12px' }}>
                  <DescriptionList isHorizontal isFluid isCompact>
                    <DescriptionListGroup>
                      <DescriptionListTerm>Delivered days</DescriptionListTerm>
                      <DescriptionListDescription>
                        {/* Labelled by the term beside it rather than by the InlineEdit's own label,
                            which would stack above the field and break the horizontal list. */}
                        <InlineEdit type='TextInput' isCompact
                          isReadOnly={isReadOnly}
                          value={klass.deliveredDays ?? ''}
                          decorator={() => klass.deliveredDays ?? 'Not recorded'}
                          onSave={(entered) => {
                            updateKlass({ ...klass, deliveredDays: parseDeliveredDays(entered) });
                            setValidationErrors(prev => ({ ...prev, deliveredDays: false }));
                          }}
                          id="deliveredDays"
                          textInputProps={{ type: 'number', min: 0, step: 0.5, 'aria-label': 'Delivered days' }} />
                      </DescriptionListDescription>
                    </DescriptionListGroup>
                    <DescriptionListGroup>
                      <DescriptionListTerm>
                        <span>Delivery days</span>{' '}
                        <Tooltip content="The days the class was actually taught on. Optional - the delivered days count above is usually enough. Record these when it matters which days they were, such as a delivery running from one month into the next, because where they are present utilization is worked out from them rather than from the count.">
                          <Icon status="info"><OutlinedQuestionCircleIcon /></Icon>
                        </Tooltip>
                      </DescriptionListTerm>
                      <DescriptionListDescription>
                        <DeliveryDaysField
                          value={klass.deliveryDays}
                          isReadOnly={isReadOnly}
                          onChange={(deliveryDays) => updateKlass({ ...klass, deliveryDays })} />
                      </DescriptionListDescription>
                    </DescriptionListGroup>
                  </DescriptionList>
                </DescriptionListDescription>
              )}
            </div>
          </DescriptionListGroup>

        </DescriptionList>
      </PageSection>

      <PageSection hasBodyWrapper isWidthLimited>
        <Tabs activeKey={activeTabKey} onSelect={handleTabClick} usePageInsets isBox={isBox} role="region">
          <Tab eventKey={TAB.OVERVIEW} title={<TabTitleText>Overview</TabTitleText>}>
            <PageSection hasBodyWrapper isWidthLimited>
              <DescriptionList columnModifier={{ default: '3Col' }}>
                <DescriptionListGroup>
                  <DescriptionListTerm>Course</DescriptionListTerm>
                  <DescriptionListDescription className='multi-line-desc-list'>
                    <DescriptionList isHorizontal isFluid isCompact>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Class SKU</DescriptionListTerm>
                        <DescriptionListDescription>
                          {/* Labelled by the term beside it rather than by the InlineEdit's own
                              label, which would stack above the field and break the horizontal
                              list this sits in. */}
                          <InlineEdit type='FilterableSelect' isCompact
                            isReadOnly={isReadOnly}
                            placeholder="Search by SKU or course name"
                            value={klass.course?.id ?? ''}
                            decorator={() => klass.course?.sku || 'Not set'}
                            onSave={onCourseChange}
                            id="course"
                            options={courseOptions} />
                        </DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Name</DescriptionListTerm>
                        <DescriptionListDescription>{klass.course?.name}</DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Version</DescriptionListTerm>
                        <DescriptionListDescription>
                          {/* Disabled rather than editable when the course has no versions to
                              offer: a pencil opening on an empty list is a dead end. */}
                          <InlineEdit type='FilterableSelect' isCompact
                            isReadOnly={isReadOnly}
                            isDisabled={versionOptions.length === 0}
                            placeholder="Select a version"
                            value={klass.version?.id ?? ''}
                            decorator={() => klass.version?.version || 'Not set'}
                            onSave={onVersionChange}
                            id="version"
                            options={versionOptions} />
                        </DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Curriculum</DescriptionListTerm>
                        <DescriptionListDescription>{klass.course?.curriculum?.name}</DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Hours per Day</DescriptionListTerm>
                        <DescriptionListDescription>{klass.version?.hoursDay}</DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Number of Days</DescriptionListTerm>
                        <DescriptionListDescription>
                          {
                            klass.course.sku.startsWith('CU-') ? 
                              'See "SKU days sold"'
                            :
                              klass.version?.duration 

                          }
                        </DescriptionListDescription>
                      </DescriptionListGroup>
                      <DescriptionListGroup>
                        <DescriptionListTerm>Hardware Level</DescriptionListTerm>
                        <DescriptionListDescription>{klass.version?.hardwareLevel}</DescriptionListDescription>
                      </DescriptionListGroup>
                    </DescriptionList>
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Facility</DescriptionListTerm>
                  <DescriptionListDescription className='multi-line-desc-list'>
                    <DescriptionList isHorizontal isFluid isCompact>
                      { klass.type === KLASS_TYPE.PRIVATE && (klass as PrivateKlass).customerLocation === true ? 
                          <DescriptionListGroup>
                            <span style={{ fontStyle: 'italic' }}>Private class at customer location</span>
                          </DescriptionListGroup>
                        :
                          <>
                            <DescriptionListGroup>
                              <DescriptionListTerm>Name</DescriptionListTerm>
                              <DescriptionListDescription>{klass.facility?.name ? klass.facility.name : 'N/A'}</DescriptionListDescription>
                            </DescriptionListGroup>
                            <DescriptionListGroup>
                              <DescriptionListTerm>Address</DescriptionListTerm>
                              <DescriptionListDescription>{displayAddress(klass.facility?.address) ? displayAddress(klass.facility?.address) : 'N/A'}</DescriptionListDescription>
                            </DescriptionListGroup>
                            <DescriptionListGroup>
                              <DescriptionListTerm>Country</DescriptionListTerm>
                              <DescriptionListDescription>{klass.facility?.address?.country?.name ? klass.facility?.address?.country?.name : 'N/A'}</DescriptionListDescription>
                            </DescriptionListGroup>
                            <DescriptionListGroup>
                              <DescriptionListTerm>Hardware Level</DescriptionListTerm>
                              <DescriptionListDescription>{facilityCapabilities.highestHardware == HARDWARE_LEVEL.NOT_APPLICABLE ? 'N/A' : facilityCapabilities.highestHardware}</DescriptionListDescription>
                            </DescriptionListGroup>
                            <DescriptionListGroup>
                              <DescriptionListTerm>Dual Monitors</DescriptionListTerm>
                              <DescriptionListDescription>{facilityCapabilities.hasDualMonitors ? 'Yes' : 'No'}</DescriptionListDescription>
                            </DescriptionListGroup>
                          </>
                      }
                    </DescriptionList>
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>TSS Name</DescriptionListTerm>
                  <DescriptionListDescription>{klass.tss?.name}</DescriptionListDescription>
                </DescriptionListGroup>
              </DescriptionList>
            </PageSection>
          </Tab>
          
          <Tab eventKey={TAB.DETAILS} title={<TabTitleText>Details</TabTitleText>}>
            <PageSection hasBodyWrapper isWidthLimited>
              <DescriptionList columnModifier={{ default: '3Col' }}>
                <DescriptionListGroup>
                  <DescriptionListTerm>Facility confirmed?</DescriptionListTerm>
                  <DescriptionListDescription>{klass.facilityConfirmed ? 'Yes' : 'No'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Delegates (Total Enrolled / Max )</DescriptionListTerm>
                  <DescriptionListDescription>
                    {/* The tooltip goes away entirely while the counts are open below the total,
                        and comes back when they are folded away: left mounted it would hang
                        around on screen saying the same thing twice. */}
                    {delegatesExpanded
                      ? <>
                          {delegatesSummary}
                          <div style={{ marginTop: '8px' }}>{delegatesBreakdown}</div>
                        </>
                      : <Tooltip position="auto" content={delegatesBreakdown}>
                          {delegatesSummary}
                        </Tooltip>
                    }
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Go No-Go Date</DescriptionListTerm>
                  <DescriptionListDescription>{ddMMyyyyFormat(klass.goNogoDate)}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>VT Labs</DescriptionListTerm>
                  <DescriptionListDescription>{klass.vtLabsRequired ? 'Yes' : 'No'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Virtual Lab Versions</DescriptionListTerm>
                  <DescriptionListDescription>{klass.virtualLabVersions?.join(', ') || 'N/A'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>
                    <span>RHLS live class?</span>{' '}
                    <Tooltip content="How the class is taught. Not the same question as the RHLS Premium modality, which is who it was sold to: a premium class can be taught in a room, and an ordinary one can be an RHLS live virtual class.">
                      <Icon status="info"><OutlinedQuestionCircleIcon /></Icon>
                    </Tooltip>
                  </DescriptionListTerm>
                  <DescriptionListDescription>{klass.rhlsLiveClass ? 'Yes' : 'No'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Language changed?</DescriptionListTerm>
                  <DescriptionListDescription>{klass.languageChanged ? 'Yes' : 'No'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Language changed requested?</DescriptionListTerm>
                  <DescriptionListDescription>{klass.languageChangeRequest ? 'Yes' : 'No'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Guarantee to run?</DescriptionListTerm>
                  <DescriptionListDescription>{klass.guarantedToRun ? 'Yes' : 'No'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Published</DescriptionListTerm>
                  <DescriptionListDescription>{klass.published ? 'Yes' : 'No'}</DescriptionListDescription>
                </DescriptionListGroup>
                {/* Region is not here: it reads beside the title, next to the class type. */}
                <DescriptionListGroup>
                  <DescriptionListTerm>Delivery responsibility</DescriptionListTerm>
                  <DescriptionListDescription>
                    {klass.deliveryResponsibility ? formatStatusText(klass.deliveryResponsibility) : 'N/A'}
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Currency</DescriptionListTerm>
                  <DescriptionListDescription>{currencyName(klass.currency) || 'N/A'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Revenue</DescriptionListTerm>
                  <DescriptionListDescription>{formatMoney(klass.revenue, klass.currency) || 'N/A'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>
                    <span>Revenue before tax</span>{' '}
                    <Tooltip content="Only the LMS reports a before-tax figure, so a private class shows one once the LMS knows about it. Absent as well on a class sold in more than one currency, where the total above is in USD.">
                      <Icon status="info"><OutlinedQuestionCircleIcon /></Icon>
                    </Tooltip>
                  </DescriptionListTerm>
                  <DescriptionListDescription>{formatMoney(klass.revenueBeforeTax, klass.currency) || 'N/A'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>
                    <span>Offering created</span>{' '}
                    <Tooltip content="When Red Hat raised the offering in the LMS. Sent as a wall clock reading with no zone on it, so it is shown as it came and is not comparable with the class's start date.">
                      <Icon status="info"><OutlinedQuestionCircleIcon /></Icon>
                    </Tooltip>
                  </DescriptionListTerm>
                  {/* ddMMyyyyHHmmFormat, not the Tz variant: there is no zone to name, and naming
                      the browser's would be a reading this class never had. */}
                  <DescriptionListDescription>{ddMMyyyyHHmmFormat(klass.offeringCreated) || 'N/A'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Domain</DescriptionListTerm>
                  <DescriptionListDescription>{klass.domain}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Reason for Cancellation</DescriptionListTerm>
                  <DescriptionListDescription>{klass.cancelledReason}</DescriptionListDescription>
                </DescriptionListGroup>
              </DescriptionList>
            </PageSection>
          </Tab>

          { klass.type === KLASS_TYPE.PRIVATE && 
            <Tab tooltip={tooltip} eventKey={TAB.PRIVATE} title={<TabTitleText>Private</TabTitleText>} disabled={klass.type !== KLASS_TYPE.PRIVATE}>
              <PrivateClassContent pklass={klass as PrivateKlass} updateKlass={updateKlass} isReadOnly={isReadOnly} />
            </Tab>
          }
          { klass.type === KLASS_TYPE.AD_HOC &&
            <Tab eventKey={TAB.AD_HOC} title={<TabTitleText>Ad Hoc</TabTitleText>} >
              <AdHocClassContent aklass={klass as AdHocKlass} />
            </Tab>
          }

          {/* Mounted only while it is the tab in front, which is what makes it free: mounting is
              what fetches, so a class page whose Survey tab is never opened costs no request. */}
          <Tab eventKey={TAB.SURVEY} title={<TabTitleText>Survey</TabTitleText>}>
            {activeTabKey === TAB.SURVEY && (
              <ClassSurveyOverview klass={klass} surveyPath={surveyPath} />
            )}
          </Tab>
        </Tabs>
      </PageSection>
      <Modal
        variant={ModalVariant.small}
        isOpen={blocker.state === 'blocked'}
        onClose={() => blocker.state === 'blocked' && blocker.reset()}
      >
        <ModalHeader>
          <Title 
            headingLevel="h1" 
            size="xl" 
            style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <Icon status="warning" size='xl'><ExclamationTriangleIcon /></Icon>
            Unsaved Changes
          </Title>
        </ModalHeader>
        <ModalBody>
          You have unsaved changes on this class. Are you sure you want to leave this page? Your changes will be lost.
        </ModalBody>
        <ModalFooter>
          <Button 
            key="confirm" 
            variant="danger" 
            onClick={() => blocker.state === 'blocked' && blocker.proceed()}
          >
            Leave without saving
          </Button>
          <Button 
            key="cancel" 
            variant="link" 
            onClick={() => blocker.state === 'blocked' && blocker.reset()}
          >
            Stay on page
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        variant={ModalVariant.small}
        isOpen={releaseConfirmOpen}
        onClose={() => setReleaseConfirmOpen(false)}
      >
        <ModalHeader>
          <Title 
            headingLevel="h1" 
            size="xl" 
            style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            {klass.released || missingFinalInstructors ? (
              <Icon status="warning" size="xl"><ExclamationTriangleIcon /></Icon>
            ) : (
              <Icon status="info" size="xl"><ExclamationCircleIcon /></Icon>
            )}
            {klass.released ? "Class Already Released" : "Release Class"}
          </Title>
        </ModalHeader>
        <ModalBody>
          {missingFinalInstructors && (
            <Alert
              variant="warning"
              isInline
              title="Fewer final instructors than allocated"
              style={{ marginBottom: '16px' }}
            >
              This class was set up for {expectedInstructorCount(klass)} instructors but only{' '}
              {klass.finalInstructors?.length ?? 0} {(klass.finalInstructors?.length ?? 0) === 1 ? 'is' : 'are'} named
              as final. Releasing it now leaves the rest off the delivery.
            </Alert>
          )}
          {klass.released
            ? "This class has already been released. Are you sure you want to release it again and potentially update its status?"
            : "Are you sure you want to release this class?"
          }
        </ModalBody>
        <ModalFooter>
          <Button 
            key="confirm-release" 
            variant="primary" 
            onClick={handleReleaseConfirm}
          >
            {klass.released ? "Yes, release again" : "Yes, release"}
          </Button>
          <Button 
            key="cancel-confirm" 
            variant="link" 
            onClick={() => setReleaseConfirmOpen(false)}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        variant={ModalVariant.small}
        isOpen={undoReleaseConfirmOpen}
        onClose={() => setUndoReleaseConfirmOpen(false)}
      >
        <ModalHeader>
          <Title
            headingLevel="h1"
            size="xl"
            style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <Icon status="warning" size="xl"><ExclamationTriangleIcon /></Icon>
            Undo Class Release
          </Title>
        </ModalHeader>
        <ModalBody>
          <Alert
            variant="warning"
            isInline
            title="Notifications may already have gone out"
            style={{ marginBottom: '16px' }}
          >
            Releasing this class may have sent a notification to the instructors and started the
            process that follows it. Undoing the release here does not recall any of that - stopping
            what is already under way has to be done by hand.
          </Alert>
          This marks the class as not released and saves it. Are you sure you want to undo the release?
        </ModalBody>
        <ModalFooter>
          <Button
            key="confirm-undo-release"
            variant="danger"
            onClick={handleUndoReleaseConfirm}
          >
            Yes, undo the release
          </Button>
          <Button
            key="cancel-undo-release"
            variant="link"
            onClick={() => setUndoReleaseConfirmOpen(false)}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </EntityChangeLog>
  );
};

export { ClassDetail };
